import {Clickable} from 'platform/foundation';

import type {MouseEvent, ReactNode} from 'react';

import {isNil, isNotNil} from 'ramda';

import {isURLDangerous, openInNewTab, useNavigate} from 'shared/web';

interface CustomerInsightsClickableRowProps {
  href: string | undefined;
  children: ReactNode;
}

const INTERACTIVE_SELECTOR = 'a, button, [role="button"]';

/**
 * Makes a whole record row open its target, so a row behaves the same in every
 * section instead of only responding to a click on its link.
 *
 * This is a mouse convenience layer only: it carries no role and no tab stop, so
 * the link inside the row stays the single element a keyboard or a screen reader
 * sees. Clicks that already landed on an interactive child are left to that child.
 */
export function CustomerInsightsClickableRow(props: CustomerInsightsClickableRowProps) {
  const navigate = useNavigate();

  // The platform Link rejects dangerous schemes on its own. This row navigates
  // without going through it, so it applies the same guard rather than trusting
  // every caller to pass a href it built itself.
  if (isNil(props.href) || isURLDangerous(props.href)) {
    return props.children;
  }

  const href = props.href;
  const isHandledByChild = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target instanceof Element ? event.target : null;

    return isNil(target) || isNotNil(target.closest(INTERACTIVE_SELECTOR));
  };

  return (
    <Clickable
      width="100%"
      onClick={(event) => {
        if (isHandledByChild(event)) {
          return;
        }
        navigate(href);
      }}
      onMouseWheelClick={(event) => {
        if (isHandledByChild(event)) {
          return;
        }
        openInNewTab(href);
      }}
    >
      {props.children}
    </Clickable>
  );
}
